import { Link } from 'react-router-dom';

export default function Terms() {
  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-2xl shadow-lg p-8 sm:p-12">
          {/* Header */}
          <div className="text-center mb-10">
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Terms of Service
            </h1>
            <p className="text-gray-500">
              Last Updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </p>
          </div>

          {/* Content */}
          <div className="prose prose-lg max-w-none text-gray-700">
            
            <p className="text-lg leading-relaxed">
              Welcome to SpeechFormation! Please read these Terms of Service ("Terms") carefully before using our website, 
              applications, or services. By accessing or using SpeechFormation, you agree to be bound by these Terms.
            </p>

            {/* Section 1 */}
            <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4">
              1. Nature of Our Platform
            </h2>
            <div className="bg-primary-50 border-l-4 border-primary-500 p-6 rounded-r-lg mb-6">
              <p className="font-semibold text-gray-900 mb-2">
                IMPORTANT: Please read this section carefully.
              </p>
              <p>
                SpeechFormation is an <strong>entertainment and gaming platform only</strong>. Our voice-controlled games 
                are designed to be fun, engaging activities for children—similar to any other game, toy, or recreational 
                product such as kinetic sand, building blocks, or video games.
              </p>
            </div>
            <p>
              <strong>SpeechFormation is NOT:</strong>
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>A medical device or medical treatment</li>
              <li>A speech therapy service or speech therapy replacement</li>
              <li>A diagnostic tool for any condition</li>
              <li>A healthcare provider or medical practice</li>
              <li>An educational institution or special education service</li>
              <li>A substitute for professional medical, therapeutic, or educational evaluation and treatment</li>
            </ul>

            {/* Section 2 */}
            <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4">
              2. No Medical Claims
            </h2>
            <p>
              SpeechFormation makes <strong>no medical claims whatsoever</strong>. We do not claim that our games will:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Diagnose, treat, cure, or prevent any disease, disorder, or condition</li>
              <li>Provide therapeutic benefits or medical outcomes</li>
              <li>Replace or supplement professional speech therapy, occupational therapy, or any other clinical treatment</li>
              <li>Address, improve, or remediate speech delays, autism spectrum disorder, or any other developmental condition</li>
              <li>Produce any specific educational, developmental, or clinical results</li>
            </ul>
            <p className="mt-4">
              Any perceived benefits from using our games are incidental to their entertainment value. If your child has 
              speech, developmental, or other health concerns, please consult with qualified healthcare professionals, 
              including licensed speech-language pathologists, pediatricians, or other appropriate specialists.
            </p>

            {/* Section 3 */}
            <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4">
              3. Third-Party Recommendations
            </h2>
            <p>
              Schools, therapists, educators, and other professionals may recommend SpeechFormation games to families 
              <strong> entirely at their own discretion</strong>, in the same manner they might recommend any toy, game, 
              book, or recreational activity. Such recommendations:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Are made independently by those professionals based on their own judgment</li>
              <li>Do not constitute an endorsement by SpeechFormation of any therapeutic approach</li>
              <li>Do not create any agency, partnership, or employment relationship with SpeechFormation</li>
              <li>Do not imply that SpeechFormation games are medical devices or therapeutic tools</li>
              <li>Are the sole responsibility of the recommending professional</li>
            </ul>

            {/* Section 4 */}
            <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4">
              4. Independent Therapist Network
            </h2>
            <p>
              SpeechFormation may facilitate connections between families and licensed speech-language pathologists, 
              occupational therapists, or other professionals ("Independent Therapists") through our platform. 
              Regarding these Independent Therapists:
            </p>
            <div className="bg-gray-50 border border-gray-200 p-6 rounded-lg my-6">
              <p className="font-semibold text-gray-900 mb-3">Independent Contractor Status</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>
                  All therapists on our platform are <strong>independent contractors</strong>, NOT employees, 
                  agents, or affiliates of SpeechFormation
                </li>
                <li>
                  SpeechFormation verifies that each therapist holds a valid professional license and maintains 
                  a copy of such license on file
                </li>
                <li>
                  Therapists provide services in their own professional capacity and are solely responsible for 
                  the services they provide
                </li>
                <li>
                  SpeechFormation does not supervise, direct, or control the professional services provided by 
                  Independent Therapists
                </li>
                <li>
                  Any professional relationship is solely between you and the Independent Therapist
                </li>
              </ul>
            </div>
            <p>
              SpeechFormation serves merely as a <strong>community platform</strong> that connects families with 
              independent licensed professionals who may provide consultation, guidance, or support. We are not 
              responsible for any advice, services, or outcomes resulting from interactions with Independent Therapists.
            </p>

            {/* Section 5 */}
            <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4">
              5. User Responsibilities
            </h2>
            <p>By using SpeechFormation, you acknowledge and agree that:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>You are using our games for entertainment purposes only</li>
              <li>You will not rely on our platform as a substitute for professional medical or therapeutic care</li>
              <li>You are responsible for supervising children's use of our platform</li>
              <li>You will seek appropriate professional help for any health, developmental, or educational concerns</li>
              <li>You assume all risks associated with using our platform</li>
            </ul>

            {/* Section 6 */}
            <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4">
              6. Disclaimer of Warranties
            </h2>
            <p className="uppercase text-sm">
              SPEECHFORMATION IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS 
              OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR 
              PURPOSE, NON-INFRINGEMENT, OR THAT USE OF THE SERVICE WILL BE UNINTERRUPTED OR ERROR-FREE. 
              SPEECHFORMATION DOES NOT WARRANT THAT THE PLATFORM WILL MEET YOUR REQUIREMENTS OR PRODUCE ANY 
              PARTICULAR RESULTS.
            </p>

            {/* Section 7 */}
            <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4">
              7. Limitation of Liability
            </h2>
            <p className="uppercase text-sm">
              TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL SPEECHFORMATION, ITS OFFICERS, 
              DIRECTORS, EMPLOYEES, AGENTS, OR AFFILIATES BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, 
              CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF PROFITS, DATA, USE, OR 
              GOODWILL, ARISING OUT OF OR RELATED TO YOUR USE OF OR INABILITY TO USE THE PLATFORM, WHETHER BASED 
              ON WARRANTY, CONTRACT, TORT (INCLUDING NEGLIGENCE), OR ANY OTHER LEGAL THEORY, EVEN IF SPEECHFORMATION 
              HAS BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES. IN NO EVENT SHALL SPEECHFORMATION'S TOTAL LIABILITY 
              TO YOU EXCEED THE AMOUNTS PAID BY YOU TO SPEECHFORMATION IN THE TWELVE (12) MONTHS PRIOR TO THE CLAIM, 
              OR ONE HUNDRED DOLLARS ($100), WHICHEVER IS GREATER.
            </p>

            {/* Section 8 */}
            <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4">
              8. Indemnification
            </h2>
            <p>
              You agree to indemnify, defend, and hold harmless SpeechFormation and its officers, directors, employees, 
              agents, and affiliates from and against any and all claims, damages, obligations, losses, liabilities, 
              costs, and expenses (including attorney's fees) arising from: (a) your use of the platform; (b) your 
              violation of these Terms; (c) your violation of any rights of another party; or (d) any claim that your 
              use of the platform caused damage to a third party.
            </p>

            {/* Section 9 */}
            <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4">
              9. Dispute Resolution and Arbitration
            </h2>
            <div className="bg-yellow-50 border-l-4 border-yellow-500 p-6 rounded-r-lg mb-6">
              <p className="font-semibold text-gray-900 mb-2">
                PLEASE READ THIS SECTION CAREFULLY – IT AFFECTS YOUR LEGAL RIGHTS
              </p>
            </div>
            <p>
              Any dispute, controversy, or claim arising out of or relating to these Terms or your use of SpeechFormation 
              shall be resolved through binding arbitration administered by the American Arbitration Association in 
              accordance with its Consumer Arbitration Rules. The arbitration shall be conducted in the English language 
              and shall take place in the state where SpeechFormation maintains its principal place of business, or at 
              another mutually agreed location.
            </p>
            <p className="mt-4">
              <strong>Class Action Waiver:</strong> YOU AGREE THAT ANY ARBITRATION OR PROCEEDING SHALL BE LIMITED TO 
              THE DISPUTE BETWEEN SPEECHFORMATION AND YOU INDIVIDUALLY. YOU WAIVE ANY RIGHT TO PARTICIPATE IN A CLASS 
              ACTION LAWSUIT OR CLASS-WIDE ARBITRATION.
            </p>

            {/* Section 10 */}
            <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4">
              10. Governing Law
            </h2>
            <p>
              These Terms shall be governed by and construed in accordance with the laws of the United States and the 
              State of Delaware, without regard to its conflict of law provisions.
            </p>

            {/* Section 11 */}
            <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4">
              11. Changes to Terms
            </h2>
            <p>
              SpeechFormation reserves the right to modify these Terms at any time. We will provide notice of material 
              changes by posting the updated Terms on our website and updating the "Last Updated" date. Your continued 
              use of the platform after such changes constitutes acceptance of the modified Terms.
            </p>

            {/* Section 12 */}
            <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4">
              12. Severability
            </h2>
            <p>
              If any provision of these Terms is found to be unenforceable or invalid, that provision shall be limited 
              or eliminated to the minimum extent necessary so that these Terms shall otherwise remain in full force 
              and effect.
            </p>

            {/* Section 13 */}
            <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4">
              13. Contact Information
            </h2>
            <p>
              If you have any questions about these Terms, please contact us at:
            </p>
            <p className="mt-2">
              <strong>Email:</strong> legal@speechformation.com<br />
              <strong>Website:</strong> www.speechformation.com
            </p>

            {/* Acknowledgment */}
            <div className="bg-gray-100 p-6 rounded-lg mt-10">
              <p className="font-semibold text-gray-900 mb-2">
                Acknowledgment
              </p>
              <p>
                By using SpeechFormation, you acknowledge that you have read, understood, and agree to be bound by 
                these Terms of Service. You further acknowledge that SpeechFormation is an entertainment platform, 
                not a medical or therapeutic service, and that you will not hold SpeechFormation liable for any 
                outcomes related to your or your child's health, development, or education.
              </p>
            </div>

          </div>

          {/* Back Link */}
          <div className="mt-10 text-center">
            <Link to="/" className="text-primary-600 hover:text-primary-700 font-medium">
              ← Back to Home
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
